import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  LinearProgress,
  Pagination,
  Stack,
  Collapse,
  Chip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  GetApp as GetAppIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { formatDateToJerusalem } from '../../../utils/dateFormatter';

const DailyReportsTab = ({
  dailyStats,
  dailyStatsLoading,
  pagination,
  selectedDays,
  setSelectedDays,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  searchTerm,
  setSearchTerm,
  itemsPerPage,
  handleItemsPerPageChange,
  handleSearch,
  handleClearFilters,
  handlePageChange,
  handleExportDailyExcel,
  handleDownloadDayData,
  exportLoading,
}) => {
  const [expandedDates, setExpandedDates] = useState(new Set());

  // Group daily stats by date
  const groupedStats = useMemo(() => {
    const groups = {};
    dailyStats.forEach((stat) => {
      const dateKey = new Date(stat.date).toDateString();
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(stat);
    });
    
    // Sort each group by creation time (if available) or by the order they appear
    Object.keys(groups).forEach(dateKey => {
      groups[dateKey].sort((a, b) => {
        // If we have creation time, sort by it, otherwise maintain original order
        if (a.createdAt && b.createdAt) {
          return new Date(a.createdAt) - new Date(b.createdAt);
        }
        return 0;
      });
    });
    
    return groups;
  }, [dailyStats]);

  const toggleDateExpansion = (dateKey) => {
    const newExpanded = new Set(expandedDates);
    if (newExpanded.has(dateKey)) {
      newExpanded.delete(dateKey);
    } else {
      newExpanded.add(dateKey);
    }
    setExpandedDates(newExpanded);
  };

  const formatCreationTime = (stat) => {
    if (stat.createdAt) {
      return formatDateToJerusalem(stat.createdAt, 'time-only');
    }
    return 'N/A';
  };
  return (
    <Card>
      <CardContent>
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          mb={2}
        >
          <Typography variant='h6'>Daily Reports</Typography>
          <Box display='flex' gap={1}>
            <Button
              variant='outlined'
              startIcon={<RefreshIcon />}
              onClick={() => handlePageChange(pagination.currentPage)}
              disabled={dailyStatsLoading}
            >
              Refresh
            </Button>
            <Button
              variant='outlined'
              startIcon={<DownloadIcon />}
              onClick={handleExportDailyExcel}
              disabled={exportLoading}
            >
              Export Daily Data
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Box mb={3}>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} md={2}>
              <FormControl size='small' fullWidth>
                <InputLabel>Days</InputLabel>
                <Select
                  value={selectedDays}
                  onChange={(e) => setSelectedDays(e.target.value)}
                  label='Days'
                >
                  <MenuItem value={7}>Last 7 days</MenuItem>
                  <MenuItem value={14}>Last 14 days</MenuItem>
                  <MenuItem value={30}>Last 30 days</MenuItem>
                  <MenuItem value={60}>Last 60 days</MenuItem>
                  <MenuItem value={90}>Last 90 days</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                size='small'
                label='Start Date'
                type='date'
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{shrink: true}}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                size='small'
                label='End Date'
                type='date'
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{shrink: true}}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                size='small'
                label='Search'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder='Search by date or count...'
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={1}>
              <FormControl size='small' fullWidth>
                <InputLabel>Per Page</InputLabel>
                <Select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(e.target.value)}
                  label='Per Page'
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box display='flex' gap={1}>
                <Button
                  variant='contained'
                  size='small'
                  onClick={handleSearch}
                  disabled={dailyStatsLoading}
                >
                  Search
                </Button>
                <Button
                  variant='outlined'
                  size='small'
                  onClick={handleClearFilters}
                  disabled={dailyStatsLoading}
                >
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Box
            mt={2}
            display='flex'
            justifyContent='space-between'
            alignItems='center'
          >
            <Typography variant='body2' color='text.secondary'>
              Showing {pagination.totalItems} results
            </Typography>
          </Box>
        </Box>

        {/* Daily Stats Table */}
        {dailyStatsLoading ? (
          <Box display='flex' justifyContent='center' p={4}>
            <CircularProgress />
          </Box>
        ) : dailyStats.length === 0 ? (
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            minHeight='200px'
          >
            <Typography variant='body1' color='textSecondary'>
              No daily statistics available for the selected period.
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align='right'>Total Data</TableCell>
                    <TableCell align='right'>Valid Data</TableCell>
                    <TableCell align='right'>Invalid Data</TableCell>
                    <TableCell align='right'>Dup - Free</TableCell>
                    <TableCell align='right'>Dup - Sent</TableCell>
                    <TableCell align='right'>Success Rate</TableCell>
                    <TableCell align='center'>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(groupedStats).map(([dateKey, stats]) => {
                    const isExpanded = expandedDates.has(dateKey);
                    const hasMultipleReports = stats.length > 1;
                    
                    // Calculate totals for the date
                    const totalData = stats.reduce((sum, stat) => sum + (stat.totalData || 0), 0);
                    const validData = stats.reduce((sum, stat) => sum + (stat.validData || 0), 0);
                    const invalidData = stats.reduce((sum, stat) => sum + (stat.invalidData || 0), 0);
                    const duplicatedFreeData = stats.reduce((sum, stat) => sum + (stat.duplicatedFreeData || 0), 0);
                    const duplicatedSentData = stats.reduce((sum, stat) => sum + (stat.duplicatedSentData || 0), 0);
                    const successRate = totalData > 0 ? ((validData / totalData) * 100).toFixed(2) : 0;
                    
                    return (
                      <React.Fragment key={dateKey}>
                        {/* Main date row */}
                        <TableRow 
                          sx={{ 
                            backgroundColor: isExpanded ? 'action.hover' : 'inherit',
                            cursor: hasMultipleReports ? 'pointer' : 'default'
                          }}
                          onClick={hasMultipleReports ? () => toggleDateExpansion(dateKey) : undefined}
                        >
                          <TableCell>
                            <Box display='flex' alignItems='center' gap={1}>
                              {hasMultipleReports && (
                                <IconButton size='small'>
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              )}
                              <Box>
                                <Typography variant='body2' fontWeight='medium'>
                                  {formatDateToJerusalem(stats[0].date, 'date-only')}
                                </Typography>
                                {hasMultipleReports && (
                                  <Chip 
                                    label={`${stats.length} reports`} 
                                    size='small' 
                                    color='primary' 
                                    variant='outlined'
                                  />
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align='right'>{totalData}</TableCell>
                          <TableCell align='right'>
                            <Box
                              display='flex'
                              alignItems='center'
                              justifyContent='flex-end'
                              gap={1}
                            >
                              <CheckCircleIcon color='success' fontSize='small' />
                              {validData}
                            </Box>
                          </TableCell>
                          <TableCell align='right'>
                            <Box
                              display='flex'
                              alignItems='center'
                              justifyContent='flex-end'
                              gap={1}
                            >
                              <ErrorIcon color='error' fontSize='small' />
                              {invalidData}
                            </Box>
                          </TableCell>
                          <TableCell align='right'>
                            <Typography variant='body2' color='warning.main' fontWeight='bold'>
                              {duplicatedFreeData}
                            </Typography>
                          </TableCell>
                          <TableCell align='right'>
                            <Typography variant='body2' color='error.main' fontWeight='bold'>
                              {duplicatedSentData}
                            </Typography>
                          </TableCell>
                          <TableCell align='right'>
                            <Box display='flex' alignItems='center' gap={1}>
                              <LinearProgress
                                variant='determinate'
                                value={parseFloat(successRate) || 0}
                                sx={{width: 60, height: 8}}
                                color={
                                  parseFloat(successRate) >= 80
                                    ? 'success'
                                    : parseFloat(successRate) >= 60
                                    ? 'warning'
                                    : 'error'
                                }
                              />
                              <Typography variant='body2' fontWeight='bold'>
                                {successRate}%
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align='center'>
                            <IconButton
                              size='small'
                              color='primary'
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadDayData(stats[0].date);
                              }}
                              disabled={exportLoading}
                              title='Download data for this day'
                            >
                              <GetAppIcon fontSize='small' />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        
                        {/* Collapsible individual reports */}
                        {hasMultipleReports && (
                          <TableRow>
                            <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                              <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                                <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                  <Typography variant='subtitle2' gutterBottom>
                                    Individual Reports for {formatDateToJerusalem(stats[0].date, 'date-only')}
                                  </Typography>
                                  <Table size='small'>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Created At</TableCell>
                                        <TableCell align='right'>Total</TableCell>
                                        <TableCell align='right'>Valid</TableCell>
                                        <TableCell align='right'>Invalid</TableCell>
                                        <TableCell align='right'>Dup - Free</TableCell>
                                        <TableCell align='right'>Dup - Sent</TableCell>
                                        <TableCell align='right'>Success Rate</TableCell>
                                        <TableCell align='center'>Actions</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {stats.map((stat, index) => (
                                        <TableRow key={`${dateKey}-${index}`}>
                                          <TableCell>
                                            <Box display='flex' alignItems='center' gap={1}>
                                              <AccessTimeIcon fontSize='small' color='action' />
                                              <Typography variant='body2'>
                                                {formatCreationTime(stat)}
                                              </Typography>
                                            </Box>
                                          </TableCell>
                                          <TableCell align='right'>{stat.totalData || 0}</TableCell>
                                          <TableCell align='right'>
                                            <Box
                                              display='flex'
                                              alignItems='center'
                                              justifyContent='flex-end'
                                              gap={1}
                                            >
                                              <CheckCircleIcon color='success' fontSize='small' />
                                              {stat.validData || 0}
                                            </Box>
                                          </TableCell>
                                          <TableCell align='right'>
                                            <Box
                                              display='flex'
                                              alignItems='center'
                                              justifyContent='flex-end'
                                              gap={1}
                                            >
                                              <ErrorIcon color='error' fontSize='small' />
                                              {stat.invalidData || 0}
                                            </Box>
                                          </TableCell>
                                          <TableCell align='right'>
                                            <Typography variant='body2' color='warning.main' fontWeight='bold'>
                                              {stat.duplicatedFreeData || 0}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align='right'>
                                            <Typography variant='body2' color='error.main' fontWeight='bold'>
                                              {stat.duplicatedSentData || 0}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align='right'>
                                            <Box display='flex' alignItems='center' gap={1}>
                                              <LinearProgress
                                                variant='determinate'
                                                value={parseFloat(stat.successRate) || 0}
                                                sx={{width: 40, height: 6}}
                                                color={
                                                  parseFloat(stat.successRate) >= 80
                                                    ? 'success'
                                                    : parseFloat(stat.successRate) >= 60
                                                    ? 'warning'
                                                    : 'error'
                                                }
                                              />
                                              <Typography variant='body2' fontWeight='bold'>
                                                {stat.successRate || 0}%
                                              </Typography>
                                            </Box>
                                          </TableCell>
                                          <TableCell align='center'>
                                            <IconButton
                                              size='small'
                                              color='primary'
                                              onClick={() => handleDownloadDayData(stat.date)}
                                              disabled={exportLoading}
                                              title='Download data for this report'
                                            >
                                              <GetAppIcon fontSize='small' />
                                            </IconButton>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Controls */}
            {pagination.totalPages > 1 && (
              <Box
                mt={3}
                display='flex'
                justifyContent='space-between'
                alignItems='center'
              >
                <Typography variant='body2' color='textSecondary'>
                  Showing{' '}
                  {(pagination.currentPage - 1) * pagination.itemsPerPage + 1}{' '}
                  to{' '}
                  {Math.min(
                    pagination.currentPage * pagination.itemsPerPage,
                    pagination.totalItems,
                  )}{' '}
                  of {pagination.totalItems} entries
                </Typography>
                <Stack spacing={2}>
                  <Pagination
                    count={pagination.totalPages}
                    page={pagination.currentPage}
                    onChange={(event, page) => handlePageChange(page)}
                    color='primary'
                    size='small'
                    showFirstButton
                    showLastButton
                    disabled={dailyStatsLoading}
                  />
                </Stack>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

DailyReportsTab.propTypes = {
  dailyStats: PropTypes.array.isRequired,
  dailyStatsLoading: PropTypes.bool.isRequired,
  pagination: PropTypes.shape({
    currentPage: PropTypes.number,
    totalPages: PropTypes.number,
    totalItems: PropTypes.number,
    itemsPerPage: PropTypes.number,
    hasNextPage: PropTypes.bool,
    hasPrevPage: PropTypes.bool,
  }).isRequired,
  selectedDays: PropTypes.number.isRequired,
  setSelectedDays: PropTypes.func.isRequired,
  startDate: PropTypes.string.isRequired,
  setStartDate: PropTypes.func.isRequired,
  endDate: PropTypes.string.isRequired,
  setEndDate: PropTypes.func.isRequired,
  searchTerm: PropTypes.string.isRequired,
  setSearchTerm: PropTypes.func.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  handleItemsPerPageChange: PropTypes.func.isRequired,
  handleSearch: PropTypes.func.isRequired,
  handleClearFilters: PropTypes.func.isRequired,
  handlePageChange: PropTypes.func.isRequired,
  handleExportDailyExcel: PropTypes.func.isRequired,
  handleDownloadDayData: PropTypes.func.isRequired,
  exportLoading: PropTypes.bool.isRequired,
};

export default DailyReportsTab;
